import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ArchObjIncidentViewModel,
  ArchObjToAddressViewModel,
  ArchObjTreeViewModel,
  FileClient,
  FmDocument,
  LocationClient,
} from '@core/api';
import {
  catchError,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SpeedikonService {
  readonly locationClient = inject(LocationClient);
  readonly fileClient = inject(FileClient);
  readonly load$ = new Subject<string>();
  readonly loadArchThree$ = new Subject<string>();
  readonly isLoading = signal(false);

  readonly retrieveDataTreed$ = this.load$.pipe(
    switchMap(id =>
      forkJoin([
        this.getArchobjById(id).pipe(
          catchError(() =>
            of({
              id: undefined,
              parentArchObjId: undefined,
              archObjType: undefined,
              changeDate: undefined,
              validFrom: undefined,
              validUntil: undefined,
              dtSite: undefined,
              isFavorite: undefined,
              dtArchObjAddress: [] as ArchObjToAddressViewModel[],
              incidents: [] as ArchObjIncidentViewModel[],
            })
          )
        ),
        this.getArchObjTree(id, 1).pipe(
          catchError(() =>
            of({
              id: undefined,
              type: undefined,
              displayName: undefined,
              children: [] as ArchObjTreeViewModel[],
            } as ArchObjTreeViewModel)
          )
        ),
      ]).pipe(
        map(([getArchobjById, getArchObjTree]) => ({
          getArchobjById,
          getArchObjTree,
        }))
      )
    ),
    shareReplay(1)
  );

  readonly isLoading$ = merge(
    this.load$.pipe(map(() => true)),
    this.retrieveDataTreed$.pipe(map(() => false))
  );

  isLoadingSignal = toSignal(this.isLoading$);

  readonly retrieveDataTreedSignal = toSignal(this.retrieveDataTreed$, {
    initialValue: {
      getArchobjById: {
        id: undefined,
        parentArchObjId: undefined,
        archObjType: undefined,
        changeDate: undefined,
        validFrom: undefined,
        validUntil: undefined,
        dtSite: undefined,
        isFavorite: undefined,
        dtArchObjAddress: [] as ArchObjToAddressViewModel[],
        incidents: [] as ArchObjIncidentViewModel[],
      },
      getArchObjTree: {
        id: undefined,
        type: undefined,
        displayName: undefined,
        children: [] as ArchObjTreeViewModel[],
        childrenCount: undefined,
      },
    },
  });

  readonly archObjIdSignal$ = new Subject<string>();

  readonly savedArchObjIdSignal = toSignal(this.archObjIdSignal$, {
    initialValue: '',
  });

  readonly documentsSignal$ = this.archObjIdSignal$.pipe(
    switchMap(archObjId => {
      if (archObjId) {
        return this.getSpeedikonDocuments(archObjId).pipe(
          catchError(() => of([] as FmDocument[]))
        );
      }
      return of([] as FmDocument[]);
    })
  );

  documentsSignalToUse = toSignal(this.documentsSignal$, {
    initialValue: [],
  });

  state = {
    treeDataSignal: this.retrieveDataTreedSignal,
    isLoading: this.isLoadingSignal,
    documents: this.documentsSignalToUse,
  };

  getArchobjById(id: string | undefined) {
    return this.locationClient.getArchobjById(id);
  }

  getArchObjTree(id: string | undefined, level: number | null | undefined) {
    return this.locationClient.getArchObjTree(id, level);
  }

  getSpeedikonDocuments(archObjId: string): Observable<FmDocument[]> {
    return this.fileClient.getSpeedikonDocuments(archObjId);
  }
}
